import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    X, Boxes, MapPin, User, FileStack, FileSignature, Truck, ShieldAlert,
    BookOpen, Recycle, History, ClipboardCheck, Package, Clock,
    Activity, Coins, Sparkles,
} from "lucide-react";
import { api } from "@/lib/api";
import { EDI } from "@/constants/testIds";

function Row({ icon: Icon, label, children }) {
    return (
        <div className="border-t border-slate-800/60 py-3 flex items-start gap-3">
            <Icon className="h-4 w-4 text-[var(--sgsb-gold)] mt-0.5 shrink-0" strokeWidth={1.5} />
            <div className="min-w-0 w-full">
                <div className="kicker text-slate-500 text-[10px]">{label}</div>
                <div className="mt-1 text-sm text-slate-200 font-body">{children}</div>
            </div>
        </div>
    );
}

export default function TwinObjectDrilldown({ tag, onClose }) {
    const [obj, setObj] = useState(null);
    const [busy, setBusy] = useState(true);

    useEffect(() => {
        setBusy(true); setObj(null);
        api.get(`/edi/twin/object/${tag}`)
            .then((r) => setObj(r.data))
            .catch(() => toast.error("Object drill-down failed"))
            .finally(() => setBusy(false));
    }, [tag]);

    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
            style={{ background: "rgba(2, 6, 23, 0.72)", backdropFilter: "blur(6px)" }}
            data-testid={EDI.twinDrilldownRoot}
        >
            <div className="glass rounded-2xl overflow-hidden w-full max-w-4xl max-h-[92vh] flex flex-col shadow-2xl">
                <div className="p-5 border-b border-slate-800/60 flex items-center gap-3">
                    <Boxes className="h-5 w-5 text-[var(--sgsb-gold)]" strokeWidth={1.5} />
                    <div className="flex-1 min-w-0">
                        <div className="kicker text-slate-500 text-[10px]">Digital Engineering Twin™ · Object</div>
                        <div className="font-display font-black text-white text-lg tracking-tight truncate">
                            {obj ? `${obj.name} · ${obj.tag}` : "Loading…"}
                        </div>
                    </div>
                    <button
                        data-testid={EDI.twinDrilldownClose}
                        onClick={onClose}
                        className="rounded-md border border-slate-700 text-slate-400 hover:text-white hover:border-slate-500 p-1.5 transition-colors duration-150"
                    >
                        <X className="h-4 w-4" strokeWidth={2} />
                    </button>
                </div>

                <div className="p-6 overflow-auto">
                    {busy && <div className="kicker text-slate-500">Loading twin object…</div>}
                    {obj && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1">
                            <Row icon={MapPin} label="Project · Client · Commodity">
                                {obj.project} · {obj.client} · {obj.commodity}
                            </Row>
                            <Row icon={MapPin} label="Plant Area · Discipline">
                                {obj.plant_area} · {obj.discipline}
                            </Row>

                            <Row icon={FileStack} label="Drawings">
                                {obj.drawings.map((d) => (
                                    <div key={d.ref}>
                                        <span className="text-slate-400">{d.ref}</span> · {d.title} <span className="text-[var(--sgsb-gold)]">Rev {d.rev}</span>
                                    </div>
                                ))}
                            </Row>
                            <Row icon={ClipboardCheck} label="Specifications">
                                {obj.specifications.map((s) => (
                                    <div key={s.ref}>
                                        <span className="text-slate-400">{s.ref}</span> · {s.title}
                                    </div>
                                ))}
                            </Row>

                            <Row icon={FileStack} label="Data Sheets">
                                {obj.data_sheets.map((d) => (
                                    <div key={d.ref}>
                                        <span className="text-slate-400">{d.ref}</span> · {d.title}
                                    </div>
                                ))}
                            </Row>
                            <Row icon={Truck} label="Vendor Packages">
                                {obj.vendor_packages.map((v) => (
                                    <div key={v.package}>
                                        <span className="text-[var(--sgsb-gold)] font-display font-bold">{v.vendor}</span> · {v.package}
                                    </div>
                                ))}
                            </Row>

                            <Row icon={ClipboardCheck} label="Inspection Reports">
                                {obj.inspection_reports.map((i) => (
                                    <div key={i.ref}>
                                        <span className="text-slate-400">{i.ref}</span> · {i.stage} · <span className="text-emerald-400">{i.result}</span>
                                    </div>
                                ))}
                            </Row>
                            <Row icon={FileSignature} label="Contracts">
                                {obj.contracts.map((c) => (
                                    <div key={c.ref}>
                                        <span className="text-slate-400">{c.ref}</span> · {c.scope} · <span className="text-[var(--sgsb-gold)]">{c.value}</span>
                                    </div>
                                ))}
                            </Row>

                            <Row icon={Package} label="Procurement">
                                {obj.procurement.map((p) => (
                                    <div key={p.po}>
                                        <span className="text-slate-400">{p.po}</span> · {p.supplier} · <span className="text-[var(--sgsb-gold)]">{p.value}</span>
                                    </div>
                                ))}
                            </Row>
                            <Row icon={BookOpen} label="Lessons Learned">
                                {obj.lessons_learned.map((l) => (
                                    <div key={l.ref}>
                                        <span className="text-slate-400">{l.ref}</span> · {l.category} · {l.note}
                                    </div>
                                ))}
                            </Row>

                            <Row icon={Recycle} label="Reuse Opportunities">
                                {obj.reuse_opportunities.map((r) => (
                                    <div key={r.target}>
                                        {r.target} · <span className="text-emerald-400">{r.confidence}%</span> confidence · {r.hours_saved.toLocaleString()} hrs
                                    </div>
                                ))}
                            </Row>
                            <Row icon={Clock} label="Estimated Saving">
                                {obj.estimated_saving.hours.toLocaleString()} hrs · <span className="text-[var(--sgsb-gold)]">{obj.estimated_saving.cost}</span>
                            </Row>

                            <Row icon={ShieldAlert} label="Associated Risks">
                                {obj.associated_risks.map((r) => (
                                    <div key={r.code}>
                                        <span className="text-slate-400">{r.code}</span> · {r.description} · <span className={r.severity === "High" ? "text-red-400" : r.severity === "Medium" ? "text-amber-400" : "text-emerald-400"}>{r.severity}</span>
                                    </div>
                                ))}
                            </Row>
                            <Row icon={User} label="Associated Engineers">
                                {obj.associated_engineers.map((e) => (
                                    <div key={e.name}>
                                        {e.name} <span className="text-slate-500">· {e.role}</span>
                                    </div>
                                ))}
                            </Row>

                            <Row icon={History} label="Revision History">
                                {obj.revision_history.map((r) => (
                                    <div key={r.rev}>
                                        <span className="text-[var(--sgsb-gold)] font-display font-bold">Rev {r.rev}</span> · {r.date} · {r.action}
                                    </div>
                                ))}
                            </Row>
                            <Row icon={ClipboardCheck} label="Approval Chain">
                                {obj.approval_chain.map((a) => (
                                    <div key={a.role}>
                                        {a.role} · {a.by} · <span className="text-emerald-400">{a.status}</span>
                                    </div>
                                ))}
                            </Row>

                            <Row icon={Activity} label="Current Status">
                                <span className="text-emerald-400 font-display font-bold">{obj.current_status || "Operational"}</span>
                            </Row>
                            <Row icon={Coins} label="Commercial Impact">
                                {obj.commercial_impact || "—"}
                            </Row>

                            <Row icon={Sparkles} label="Knowledge Value">
                                <span className="text-[var(--sgsb-gold)] font-display font-bold">{obj.knowledge_value || "—"}</span>
                            </Row>
                            <Row icon={Recycle} label="Reuse History">
                                {(obj.reuse_history || []).length === 0
                                    ? <span className="text-slate-500">No prior reuse recorded</span>
                                    : obj.reuse_history.map((r, i) => (
                                        <div key={i}>
                                            {r.project} <span className="text-slate-500">· {r.date}</span> · <span className="text-emerald-400">{r.hours_saved.toLocaleString()} hrs saved</span>
                                        </div>
                                    ))
                                }
                            </Row>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
